import { DerbyRngV1 } from '../rng';
import { DRILL_TICK_S, MASK_PRESSED, type MinigameDef, type MinigameSession, type SessionScore } from './framework';

export interface TempoBeat { tick: number; hit: boolean | null }

/** Stamina rhythm: hit changing hoofbeats without rushing the breath. */
export class TempoSession implements MinigameSession {
  readonly durationTicks = Math.round(15 / DRILL_TICK_S);
  readonly beats: TempoBeat[];
  tick = 0;
  breath = 1;
  combo = 0;
  bestCombo = 0;
  hits = 0;
  misses = 0;
  private cursor = 0;

  constructor(readonly tier: number, seed: number) {
    const rng = new DerbyRngV1(seed).fork('tempo');
    const beats: TempoBeat[] = [];
    let at = Math.round(0.9 / DRILL_TICK_S);
    while (at < this.durationTicks - 8) {
      beats.push({ tick: at, hit: null });
      const cadence = rng.range(0.52 - tier * 0.025, 0.82 - tier * 0.025);
      at += Math.round(cadence / DRILL_TICK_S);
    }
    this.beats = beats;
  }

  get finished(): boolean { return this.tick >= this.durationTicks || this.breath <= 0; }
  nextBeat(): TempoBeat | null { return this.beats[this.cursor] ?? null; }
  nextBeatIndex(): number { return this.cursor; }
  timingWindowTicks(): number { return Math.max(3, 7 - this.tier); }

  step(mask: number): void {
    if (this.finished) return;
    const window = this.timingWindowTicks();
    const pressed = (mask & MASK_PRESSED) !== 0;
    const beat = this.beats[this.cursor];
    if (pressed) {
      if (beat && Math.abs(this.tick - beat.tick) <= window) {
        beat.hit = true; this.hits += 1; this.combo += 1; this.bestCombo = Math.max(this.bestCombo, this.combo); this.cursor += 1;
      } else {
        this.breath = Math.max(0, this.breath - 0.09); this.combo = 0;
      }
    }
    const due = this.beats[this.cursor];
    if (due && this.tick > due.tick + window) {
      due.hit = false; this.misses += 1; this.combo = 0; this.breath = Math.max(0, this.breath - 0.06); this.cursor += 1;
    }
    this.breath = Math.min(1, this.breath + 0.002);
    this.tick += 1;
  }

  score(): SessionScore {
    const accuracy = this.beats.length ? this.hits / this.beats.length : 0;
    const raw = Math.max(0, Math.min(1, 0.8 * accuracy + 0.15 * this.breath + Math.min(0.05, this.bestCombo * 0.005)));
    return { raw, detail: `${this.hits}/${this.beats.length} beats · rhythm ×${this.bestCombo} · breath ${Math.round(this.breath * 100)}%`, failed: this.breath <= 0 };
  }
}

export const TEMPO_DEF: MinigameDef = {
  id: 'tempo', name: 'Tempo Miles', stat: 'stamina', energyCost: 4,
  create: (tier, seed) => new TempoSession(tier, seed),
};
